import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { OpeningLead } from './components/OpeningLead';
import { LiveActivityPopup } from './components/LiveActivityPopup';
import { LuxuryStickyBar } from './components/LuxuryStickyBar';
import { TopFlashBar } from './components/TopFlashBar';
import { initMetaPixel, trackPageView } from './utils/metaPixel';
// Lazy load below-the-fold components
const EmpathySection = lazy(() => import('./components/EmpathySection').then(m => ({
  default: m.EmpathySection
})));
const FailedSolutions = lazy(() => import('./components/FailedSolutions').then(m => ({
  default: m.FailedSolutions
})));
const RevelationSection = lazy(() => import('./components/RevelationSection').then(m => ({
  default: m.RevelationSection
})));
const ProductContents = lazy(() => import('./components/ProductContents').then(m => ({
  default: m.ProductContents
})));
const ObjectionsSection = lazy(() => import('./components/ObjectionsSection').then(m => ({
  default: m.ObjectionsSection
})));
const TestimonialsSection = lazy(() => import('./components/TestimonialsSection').then(m => ({
  default: m.TestimonialsSection
})));
const CTASection = lazy(() => import('./components/CTASection').then(m => ({
  default: m.CTASection
})));
const ClosingSection = lazy(() => import('./components/ClosingSection').then(m => ({
  default: m.ClosingSection
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
function SectionLoader() {
  return <div className="w-full h-32 animate-pulse bg-[var(--bg-elevated)]/50" data-id="element-0" />;
}
export function App() {
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    initMetaPixel();
  }, []);
  // Fire PageView when user scrolls to the bottom of the page
  useEffect(() => {
    const el = bottomRef.current;
    if (!el) return;
    let hasFired = false;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !hasFired) {
        hasFired = true;
        trackPageView();
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <main className="w-full min-h-screen bg-[var(--bg-deep)] text-[var(--text-primary)] overflow-x-hidden selection:bg-[var(--accent-soft)] selection:text-[var(--text-heading)]" data-id="element-1">
      <TopFlashBar data-id="element-2" />
      <LuxuryStickyBar data-id="element-3" />
      <LiveActivityPopup data-id="element-4" />

      {/* Above the fold - eager loaded */}
      <OpeningLead data-id="element-5" />

      <div className="sage-divider" data-id="element-6" />

      {/* Below the fold - lazy loaded */}
      <Suspense fallback={<SectionLoader data-id="element-8" />} data-id="element-7">
        <EmpathySection data-id="element-9" />
        <FailedSolutions data-id="element-10" />

        <div className="sage-divider" data-id="element-11" />

        <RevelationSection data-id="element-12" />
        <TestimonialsSection data-id="element-13" />
        <ProductContents data-id="element-14" />

        <div className="sage-divider" data-id="element-15" />

        <CTASection data-id="element-16" />
        <ObjectionsSection data-id="element-17" />
        <ClosingSection data-id="element-18" />
        <Footer data-id="element-19" />
      </Suspense>

      {/* Invisible marker to detect full page view */}
      <div ref={bottomRef} aria-hidden="true" data-id="element-20" />
    </main>;
}